import { createReducer } from "typesafe-actions";

import type {
  FiWeeklySummaryPreset,
  FiWordCloudPreset,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";

import type CalendarSyncAffectedUser from "@app/types/nylas/CalendarSyncAffectedUser";

import {
  Actions,
  applyExecutiveSummaryPreset,
  applyWordCloudPreset,
  clearAffectedUsers,
  deleteExecutiveSummaryPreset,
  deleteWordCloudPreset,
  fetchAffectedUsers,
  fetchExecutiveSummaryPresets,
  fetchWordCloudPresets,
  refreshExecutiveSummaryPresets,
  saveExecutiveSummaryPreset,
} from "./actions";

export interface OrganizationDetailsState {
  affectedUsers: CalendarSyncAffectedUser[];
  affectedUsersLoading: boolean;
  affectedUsersTotal: number;
  // Only roles that actually have a preset are returned, so a missing role means "not set"
  // rather than "still loading".
  executiveSummaryPresets: FiWeeklySummaryPreset[];
  // A failed read leaves the list empty, which reads exactly like an org with no presets — and
  // the only action offered from that state overwrites a preset that is actually there.
  executiveSummaryPresetsFailed: boolean;
  executiveSummaryPresetsLoading: boolean;
  executiveSummaryPresetsOrganizationId: Organization["id"] | undefined;
  executiveSummaryPresetsUpdating: boolean;
  wordCloudPresets: FiWordCloudPreset[];
  wordCloudPresetsFailed: boolean;
  wordCloudPresetsLoading: boolean;
  wordCloudPresetsOrganizationId: Organization["id"] | undefined;
  wordCloudPresetsUpdating: boolean;
}

const initialState: OrganizationDetailsState = {
  affectedUsers: [],
  affectedUsersLoading: false,
  affectedUsersTotal: 0,
  executiveSummaryPresets: [],
  executiveSummaryPresetsFailed: false,
  executiveSummaryPresetsLoading: false,
  executiveSummaryPresetsOrganizationId: undefined,
  executiveSummaryPresetsUpdating: false,
  wordCloudPresets: [],
  wordCloudPresetsFailed: false,
  wordCloudPresetsLoading: false,
  wordCloudPresetsOrganizationId: undefined,
  wordCloudPresetsUpdating: false,
};

const organizationDetails = createReducer<OrganizationDetailsState, Actions>(initialState)
  .handleAction(fetchAffectedUsers.request, (state) => ({
    ...state,
    affectedUsers: [],
    affectedUsersLoading: true,
    affectedUsersTotal: 0,
  }))
  .handleAction(fetchAffectedUsers.success, (state, { payload }) => ({
    ...state,
    affectedUsers: payload.users,
    affectedUsersLoading: false,
    affectedUsersTotal: payload.total,
  }))
  .handleAction(fetchAffectedUsers.failure, (state) => ({
    ...state,
    affectedUsersLoading: false,
  }))
  .handleAction(clearAffectedUsers, () => initialState)
  .handleAction(fetchExecutiveSummaryPresets.request, (state) => ({
    ...state,
    executiveSummaryPresetsFailed: false,
    executiveSummaryPresetsLoading: true,
  }))
  .handleAction(refreshExecutiveSummaryPresets.request, (state) => ({
    ...state,
    executiveSummaryPresetsLoading: true,
  }))
  .handleAction(
    [fetchExecutiveSummaryPresets.success, refreshExecutiveSummaryPresets.success],
    (state, { payload }) => ({
      ...state,
      executiveSummaryPresets: payload.presets,
      executiveSummaryPresetsFailed: false,
      executiveSummaryPresetsLoading: false,
      executiveSummaryPresetsOrganizationId: payload.organizationId,
    })
  )
  .handleAction(fetchExecutiveSummaryPresets.failure, (state) => ({
    ...state,
    executiveSummaryPresetsFailed: true,
    executiveSummaryPresetsLoading: false,
  }))
  // A recovery read that fails leaves the cards showing what they had. They may now disagree with
  // the server, but that is a smaller lie than a section-wide "couldn't load presets" raised by a
  // save — and the save's own error has already been reported.
  .handleAction(refreshExecutiveSummaryPresets.failure, (state) => ({
    ...state,
    executiveSummaryPresetsLoading: false,
  }))
  .handleAction(
    [
      applyExecutiveSummaryPreset.request,
      deleteExecutiveSummaryPreset.request,
      saveExecutiveSummaryPreset.request,
    ],
    (state) => ({ ...state, executiveSummaryPresetsUpdating: true })
  )
  .handleAction(
    [
      applyExecutiveSummaryPreset.failure,
      applyExecutiveSummaryPreset.success,
      deleteExecutiveSummaryPreset.failure,
      deleteExecutiveSummaryPreset.success,
      saveExecutiveSummaryPreset.failure,
      saveExecutiveSummaryPreset.success,
    ],
    (state) => ({ ...state, executiveSummaryPresetsUpdating: false })
  )
  .handleAction(fetchWordCloudPresets.request, (state) => ({
    ...state,
    wordCloudPresetsFailed: false,
    wordCloudPresetsLoading: true,
  }))
  .handleAction(fetchWordCloudPresets.success, (state, { payload }) => ({
    ...state,
    wordCloudPresets: payload.presets,
    wordCloudPresetsLoading: false,
    wordCloudPresetsOrganizationId: payload.organizationId,
  }))
  .handleAction(fetchWordCloudPresets.failure, (state) => ({
    ...state,
    wordCloudPresetsFailed: true,
    wordCloudPresetsLoading: false,
  }))
  .handleAction([applyWordCloudPreset.request, deleteWordCloudPreset.request], (state) => ({
    ...state,
    wordCloudPresetsUpdating: true,
  }))
  .handleAction(
    [
      applyWordCloudPreset.failure,
      applyWordCloudPreset.success,
      deleteWordCloudPreset.failure,
      deleteWordCloudPreset.success,
    ],
    (state) => ({ ...state, wordCloudPresetsUpdating: false })
  );

export * from "./selectors";
export type OrganizationDetailsActions = Actions;
export default organizationDetails;
